// VendorRiderTrackingMap.js
// Live rider tracking for in-house delivery agents — scoped to the logged-in vendor.
// Modelled after ThreePL/RiderTrackingMap.js but uses the vendor socket room and
// the generic /riders/vendor/:vendorId/riders endpoint instead of the 3PL partner API.

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import { Card, Row, Col } from 'reactstrap';
import { Spinner } from 'react-bootstrap';
import socket from '../../utilities/socket';
import { attachVendorOrderRooms } from '../../hooks/useVendorOrderRooms';
import { vendorOwnerIdOf } from '../../utilities/vendorScope';
import RiderFleetMap from '../../components/Maps/RiderFleetMap';
import { toast } from 'react-toastify';
import { getUserFromLocalStorage } from '../../utilities/user';
import { FaMapMarkedAlt, FaBicycle, FaMotorcycle, FaCar, FaTruck, FaShuttleVan } from 'react-icons/fa';
import {
    isFreshRiderLocation,
    normalizeRiderLocation,
    riderLocationAgeLabel,
} from '../../utilities/riderMapLocation';

// ── Palette ────────────────────────────────────────────────────────────────────
const C = {
    blue:    '#023485ff',
    green:   '#10b981',
    yellow:  '#f59e0b',
    red:     '#ef4444',
    gray:    '#6b7280',
    white:   '#ffffff',
    darkBg:  '#0f1923',
    cardBg:  '#1a2636',
    border:  '#243447',
};

// ── CSS ────────────────────────────────────────────────────────────────────────
const MAP_CSS = `
  .vrtm-live-badge { display:inline-flex;align-items:center;gap:6px;background:rgba(15,25,35,0.88);backdrop-filter:blur(8px);border:1px solid rgba(255,255,255,0.1);border-radius:20px;padding:5px 12px;color:#fff;font-size:0.72rem;font-weight:700; }
  .vrtm-live-dot { width:8px;height:8px;border-radius:50%;background:#10b981;box-shadow:0 0 0 0 rgba(16,185,129,0.7);animation:vrtm-pulse 1.8s infinite; }
  .vrtm-live-dot.off { background:#ef4444;box-shadow:none;animation:none; }
  @keyframes vrtm-pulse { 0%{box-shadow:0 0 0 0 rgba(16,185,129,0.7)} 70%{box-shadow:0 0 0 8px rgba(16,185,129,0)} 100%{box-shadow:0 0 0 0 rgba(16,185,129,0)} }
  @keyframes vrtm-ring-pulse { 0%{transform:scale(1);opacity:0.8} 100%{transform:scale(2.4);opacity:0} }
  .vrtm-pulse-ring { animation:vrtm-ring-pulse 1.6s ease-out infinite; }

  .vrtm-stat-pill { background:rgba(15,25,35,0.88);backdrop-filter:blur(8px);border:1px solid rgba(255,255,255,0.1);border-radius:20px;padding:5px 14px;display:flex;align-items:center;gap:7px;color:#fff;font-size:0.72rem;font-weight:700;white-space:nowrap; }
  .vrtm-stat-dot { width:8px;height:8px;border-radius:50%;flex-shrink:0; }
  .vrtm-stat-dot.green  { background:#10b981;box-shadow:0 0 6px #10b981; }
  .vrtm-stat-dot.yellow { background:#f59e0b;box-shadow:0 0 6px #f59e0b; }
  .vrtm-stat-dot.gray   { background:#6b7280; }
  .vrtm-stat-dot.blue   { background:#5bc5f5;box-shadow:0 0 6px #5bc5f5; }

  .vrtm-map-header { background:linear-gradient(135deg,#0f1923 0%,#023485ff 100%);padding:12px 18px;color:#fff;display:flex;align-items:center;justify-content:space-between; }
  .vrtm-map-title { font-weight:800;font-size:0.9rem;letter-spacing:0.04em; }
  .vrtm-map-updated { font-size:0.68rem;color:rgba(255,255,255,0.55);margin-top:2px; }

  .vrtm-panel-header { background:linear-gradient(135deg,#0f1923 0%,#023485ff 100%);padding:14px 18px;color:#fff;display:flex;align-items:center;justify-content:space-between; }
  .vrtm-panel-title { font-weight:800;font-size:0.9rem;letter-spacing:0.04em;display:flex;align-items:center;gap:8px; }

  .vrtm-rider-item { padding:12px 14px;border-left:4px solid transparent;transition:background 0.15s;cursor:pointer;border-bottom:1px solid #f0f4f8; }
  .vrtm-rider-item:hover { background:#f0f9ff; }
  .vrtm-rider-item.selected { background:#e8f4fd;border-left-color:#023485ff; }
  .vrtm-rider-item.online  { border-left-color:#10b981; }
  .vrtm-rider-item.offline { border-left-color:#d1d5db; }
  .vrtm-rider-item.busy    { border-left-color:#f59e0b; }

  .vrtm-avatar { width:36px;height:36px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-weight:800;font-size:0.78rem;color:#fff;flex-shrink:0; }
  .vrtm-rider-name { font-weight:700;font-size:0.84rem;color:#1a2636;line-height:1.2; }
  .vrtm-rider-sub  { font-size:0.7rem;color:#9aacbf;margin-top:1px; }
  .vrtm-rider-loc  { font-size:0.7rem;color:#023485ff;font-weight:600;margin-top:4px; }

  .vrtm-badge { display:inline-block;padding:2px 8px;border-radius:20px;font-size:0.62rem;font-weight:800;letter-spacing:0.04em;text-transform:uppercase; }
  .vrtm-badge-online  { background:#d1fae5;color:#065f46; }
  .vrtm-badge-offline { background:#f3f4f6;color:#4b5563; }
  .vrtm-badge-busy    { background:#fef3c7;color:#92400e; }

  .vrtm-popup { min-width:200px;font-family:inherit; }
  .vrtm-popup-name { font-weight:800;font-size:0.92rem;color:#0f1923;margin-bottom:2px; }
  .vrtm-popup-carrier { font-size:0.72rem;color:#6b7280;margin-bottom:8px; }
  .vrtm-popup-row { display:flex;justify-content:space-between;align-items:center;font-size:0.75rem;padding:3px 0;border-bottom:1px solid #f0f4f8; }
  .vrtm-popup-row:last-child { border-bottom:none; }
  .vrtm-popup-label { color:#8a9ab0;font-weight:600; }
  .vrtm-popup-val   { font-weight:700;color:#1a2636; }
  .vrtm-popup-status-online  { color:#10b981; }
  .vrtm-popup-status-offline { color:#6b7280; }
  .vrtm-popup-status-busy    { color:#f59e0b; }

  .vrtm-empty { padding:48px;text-align:center;color:#6b7280;font-size:0.84rem; }

  .gm-style .gm-style-iw-c { border-radius:10px !important;box-shadow:0 8px 24px rgba(0,0,0,0.18) !important;padding:12px !important; }
  .gm-style .gm-style-iw-tc { display:none !important; }
  .rider-marker { transition:transform 0.4s linear; }
`;

// ── Helpers ───────────────────────────────────────────────────────────────────
const getLatLng = (location) => {
    const normalized = normalizeRiderLocation(location);
    return normalized ? { lat: normalized.lat, lng: normalized.lng } : null;
};

// Position accessor handed to RiderFleetMap. Kept here because the vendor feed
// normalises its locations through utilities/riderMapLocation.
const getRiderPosition = (rider) => getLatLng(rider.currentLocation);

// ── Rider presenters (shared by the side panel and the map popup) ─────────────
const initials = (r) =>
    ((r.firstName?.[0] || '') + (r.lastName?.[0] || '')).toUpperCase() || '?';

const avatarColor = (r) =>
    r.onlineStatus === 'Online'
        ? (r.currentActiveOrders > 0 ? C.yellow : C.green)
        : C.gray;

const statusCls = (s) =>
    s === 'Online' ? 'online' : s === 'Busy' ? 'busy' : 'offline';

const carrierIcon = (type) => {
    const s = { verticalAlign: '-2px', marginRight: 2 };
    switch ((type || '').toLowerCase()) {
        case 'bicycle':    return <FaBicycle style={s} />;
        case 'motorcycle': return <FaMotorcycle style={s} />;
        case 'car':        return <FaCar style={s} />;
        case 'truck':      return <FaTruck style={s} />;
        case 'van':        return <FaShuttleVan style={s} />;
        default:           return <FaMotorcycle style={s} />;
    }
};

// ── Component ─────────────────────────────────────────────────────────────────
const VendorRiderTrackingMap = () => {
    const { agent }  = useSelector((state) => state.auth);
    const localUser  = getUserFromLocalStorage();
    const user       = agent || localUser;
    // The owner's id (4 Oct 2026): a team member tracks the business's riders.
    const vendorId   = vendorOwnerIdOf(user);
    const token      = user?.token;

    const [riders,        setRiders]        = useState([]);
    const [loading,       setLoading]       = useState(true);
    const [connected,     setConnected]     = useState(socket.connected);
    const [selectedRider, setSelectedRider] = useState(null);
    const [lastUpdate,    setLastUpdate]    = useState(null);
    const [locationNames, setLocationNames] = useState({});
    const locationNamesRef = useRef({});
    const [riderTrails,   setRiderTrails]   = useState({});
    const [clockNow,      setClockNow]      = useState(Date.now());
    const MAX_TRAIL = 20;

    const API_URL = process.env.REACT_APP_SERVER_URL || '';

    useEffect(() => {
        const timer = setInterval(() => setClockNow(Date.now()), 30000);
        return () => clearInterval(timer);
    }, []);

    // ── Reverse geocode (cached) ──────────────────────────────────────────────
    const reverseGeocode = useCallback(async (lat, lng) => {
        const key = `${lat.toFixed(5)},${lng.toFixed(5)}`;
        if (locationNamesRef.current[key]) return locationNamesRef.current[key];
        try {
            const res  = await fetch(`${API_URL}/api/places/nearby?lat=${lat}&lng=${lng}`);
            if (res.ok) {
                const data = await res.json();
                const estTypes = ['establishment','point_of_interest','lodging','food','store','health','school','place_of_worship','premise','restaurant','cafe','bar','hospital','pharmacy'];
                const places = (data.results || []).filter(r => r.types.some(t => estTypes.includes(t)));
                let best = null, minD = Infinity;
                for (const p of places) {
                    const d = Math.pow((p.geometry?.location?.lat || 0) - lat, 2) + Math.pow((p.geometry?.location?.lng || 0) - lng, 2);
                    if (d < minD) { minD = d; best = p; }
                }
                const label = best?.name
                    ? (best.vicinity ? `${best.name}, ${best.vicinity.split(',')[0]}` : best.name)
                    : null;
                if (label) {
                    locationNamesRef.current[key] = label;
                    setLocationNames(prev => ({ ...prev, [key]: label }));
                    return label;
                }
            }
        } catch (_) { /* silent */ }
        return null;
    }, [API_URL]);

    useEffect(() => {
        if (!riders.length) return;
        (async () => {
            for (const r of riders) {
                const loc = getLatLng(r.currentLocation);
                if (loc && loc.lat !== 0 && loc.lng !== 0) await reverseGeocode(loc.lat, loc.lng);
            }
        })();
    }, [riders, reverseGeocode]);

    // ── Fetch riders ──────────────────────────────────────────────────────────
    const fetchRiders = useCallback(async () => {
        if (!vendorId || !token) { setLoading(false); return; }
        try {
            const res  = await fetch(`${API_URL}/riders/vendor/${vendorId}/riders`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            const data = await res.json();
            if (data.success !== false) {
                const list = data.data?.riders || data.riders || data || [];
                const nextRiders = Array.isArray(list) ? list : [];
                setRiders(nextRiders);
                const latestTimestamp = nextRiders.reduce((latest, rider) => {
                    const normalized = normalizeRiderLocation(rider.currentLocation);
                    const timestamp = normalized?.lastUpdated
                        ? new Date(normalized.lastUpdated).getTime()
                        : 0;
                    return Number.isFinite(timestamp) ? Math.max(latest, timestamp) : latest;
                }, 0);
                if (latestTimestamp > 0) setLastUpdate(new Date(latestTimestamp));
            }
        } catch (e) {
            console.error('VendorRiderTrackingMap: fetch riders failed', e);
            toast.error('Failed to load riders');
        } finally {
            setLoading(false);
        }
    }, [vendorId, token, API_URL]);

    // ── Socket ────────────────────────────────────────────────────────────────
    useEffect(() => {
        if (!vendorId) { setLoading(false); return; }

        // attachVendorOrderRooms (below) rejoins on every connect.
        const onConnect = () => setConnected(true);
        const onDisconnect = () => setConnected(false);

        const onLocationUpdated = (data) => {
            const normalized = normalizeRiderLocation({
                ...data?.location,
                lastUpdated: data?.timestamp || new Date().toISOString(),
            });
            if (!data?.riderId || !normalized) return;
            setRiders(prev => prev.map(r =>
                r._id === data.riderId
                    ? { ...r, currentLocation: normalized }
                    : r
            ));
            setRiderTrails(prev => {
                const existing = prev[data.riderId] || [];
                const updated  = [...existing, [normalized.lat, normalized.lng]];
                return { ...prev, [data.riderId]: updated.slice(-MAX_TRAIL) };
            });
            setLastUpdate(new Date(normalized.lastUpdated));
            setClockNow(Date.now());
        };

        const onStatusChanged = ({ riderId, status, isAvailable }) => {
            setRiders(prev => prev.map(r =>
                r._id === riderId ? { ...r, onlineStatus: status, availability: { ...r.availability, isAvailable } } : r
            ));
        };

        const onHeartbeat = ({ riderId, lastActiveAt }) => {
            setRiders(prev => prev.map(r => r._id === riderId ? { ...r, lastActiveAt } : r));
        };

        socket.on('connect',              onConnect);
        socket.on('disconnect',           onDisconnect);
        socket.on('rider:locationUpdated', onLocationUpdated);
        socket.on('rider:statusChanged',   onStatusChanged);
        socket.on('rider:heartbeatUpdate', onHeartbeat);

        // One join names the owner and the server picks the rooms (4 Oct 2026):
        // the whole business for head office, the member's branch rooms for a
        // member limited to some branches. A server older than this page gets
        // the old join. The rooms stay joined when the map closes: the navbar
        // and the order screens share this socket and listen in them too.
        const stopRooms = attachVendorOrderRooms(socket, vendorId, {
            fallback: (s) => s.emit('joinVendorRoom', vendorId),
        });
        if (socket.connected) setConnected(true);
        else socket.connect();

        fetchRiders();

        return () => {
            socket.off('connect',              onConnect);
            socket.off('disconnect',           onDisconnect);
            socket.off('rider:locationUpdated', onLocationUpdated);
            socket.off('rider:statusChanged',   onStatusChanged);
            socket.off('rider:heartbeatUpdate', onHeartbeat);
            stopRooms();
        };
    }, [vendorId, fetchRiders]);

    // ── Derived ───────────────────────────────────────────────────────────────
    const ridersWithLoc = riders.filter(r => isFreshRiderLocation(r.currentLocation, clockNow));
    const online  = riders.filter(r => r.onlineStatus === 'Online');
    const offline = riders.filter(r => r.onlineStatus !== 'Online');


    // ── Marker popup ──────────────────────────────────────────────────────────
    // Handed to RiderFleetMap, which renders it into the Google info window and
    // re-renders it while the window is open so speed and last ping stay live.
    const renderRiderPopup = useCallback((rider) => {
        const loc       = getRiderPosition(rider);
        const cacheKey  = loc ? `${loc.lat.toFixed(5)},${loc.lng.toFixed(5)}` : null;
        const locName   = cacheKey ? locationNames[cacheKey] : null;
        const speed     = rider.currentLocation?.speed;
        const updatedAt = rider.currentLocation?.lastUpdated;
        const sCls      = statusCls(rider.onlineStatus);

        return (
            <div className="vrtm-popup">
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                    <div className="vrtm-avatar" style={{ background: avatarColor(rider), width: 38, height: 38, fontSize: '0.8rem' }}>
                        {initials(rider)}
                    </div>
                    <div>
                        <div className="vrtm-popup-name">{rider.firstName} {rider.lastName}</div>
                        <div className="vrtm-popup-carrier">{carrierIcon(rider.carrierType)} {rider.carrierType}</div>
                    </div>
                </div>
                <div className="vrtm-popup-row">
                    <span className="vrtm-popup-label">Status</span>
                    <span className={`vrtm-popup-val vrtm-popup-status-${sCls}`}>{rider.onlineStatus}</span>
                </div>
                <div className="vrtm-popup-row">
                    <span className="vrtm-popup-label">Active Orders</span>
                    <span className="vrtm-popup-val">{rider.currentActiveOrders || 0}</span>
                </div>
                {speed > 0 && (
                    <div className="vrtm-popup-row">
                        <span className="vrtm-popup-label">Speed</span>
                        <span className="vrtm-popup-val">{Math.round(speed)} km/h</span>
                    </div>
                )}
                {locName && (
                    <div className="vrtm-popup-row">
                        <span className="vrtm-popup-label">Location</span>
                        <span className="vrtm-popup-val" style={{ maxWidth: 120, textAlign: 'right' }}>{locName}</span>
                    </div>
                )}
                {updatedAt && (
                    <div style={{ marginTop: 6, fontSize: '0.65rem', color: '#9aacbf', textAlign: 'right' }}>
                        Pinged {new Date(updatedAt).toLocaleTimeString()}
                    </div>
                )}
            </div>
        );
    }, [locationNames]);

    // ── Loading ───────────────────────────────────────────────────────────────
    if (loading) {
        return (
            <div className="content d-flex justify-content-center align-items-center" style={{ minHeight: 400 }}>
                <Spinner animation="grow" style={{ color: C.blue }} />
            </div>
        );
    }

    // ── Render ────────────────────────────────────────────────────────────────
    return (
        <div className="content">
            <style>{MAP_CSS}</style>

            {/* ── Page header ──────────────────────────────────────────────── */}
            <Row className="mb-3" style={{ alignItems: 'center' }}>
                <Col>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div style={{ width: 40, height: 40, borderRadius: 10, background: 'linear-gradient(135deg,#0f1923,#023485ff)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <FaMapMarkedAlt style={{ color: '#fff', fontSize: 20 }} />
                        </div>
                        <div>
                            <h4 style={{ margin: 0, color: C.blue, fontWeight: 800, fontSize: '1.15rem' }}>Live Rider Tracking</h4>
                            <p style={{ margin: 0, color: C.gray, fontSize: '0.78rem' }}>Real-time location of your in-house delivery fleet</p>
                        </div>
                    </div>
                </Col>
                <Col className="text-end">
                    <span className="vrtm-live-badge">
                        <span className={`vrtm-live-dot${connected ? '' : ' off'}`} />
                        {connected ? 'Live' : 'Disconnected'}
                    </span>
                    {lastUpdate && (
                        <div style={{ color: C.gray, fontSize: '0.68rem', marginTop: 4 }}>
                            Updated {lastUpdate.toLocaleTimeString()}
                        </div>
                    )}
                </Col>
            </Row>

            {/* ── How 3PL partners track riders — educational callout ───────── */}
            <div style={{
                background: 'linear-gradient(135deg, #eef4ff 0%, #f0fdf4 100%)',
                border: '1px solid #c7d7f8',
                borderRadius: 12,
                padding: '14px 20px',
                marginBottom: 20,
                display: 'flex',
                gap: 16,
                alignItems: 'flex-start',
            }}>
                <div style={{ fontSize: 28, lineHeight: 1, marginTop: 2, flexShrink: 0 }}>
                    <FaMapMarkedAlt style={{ color: C.blue }} />
                </div>
                <div>
                    <div style={{ fontWeight: 800, color: C.blue, fontSize: '0.88rem', marginBottom: 4 }}>
                        How 3PL partners track their riders — and how you can too
                    </div>
                    <div style={{ color: '#374151', fontSize: '0.80rem', lineHeight: 1.6 }}>
                        Logistics companies use a{' '}
                        <strong>vehicle map + side panel</strong> pattern: every active rider is a live pin on the
                        map, colour-coded by status (green = free, amber = on an order, grey = offline). The
                        side panel lists riders in order of status so dispatchers can instantly spot who is
                        available. Clicking a pin shows a popup with speed, ETA, and last GPS ping. A dashed
                        trail behind the pin shows the last few positions so you can see direction of travel at
                        a glance. This is exactly what is shown below — live from the Suku Rider App.
                    </div>
                </div>
            </div>

            <Row>
                {/* ── Map panel ──────────────────────────────────────────────── */}
                <Col lg="8" style={{ marginBottom: 20 }}>
                    <Card style={{ borderRadius: 12, overflow: 'hidden', border: 'none', boxShadow: '0 4px 24px rgba(0,0,0,0.12)' }}>
                        <div className="vrtm-map-header">
                            <div>
                                <div className="vrtm-map-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <FaMapMarkedAlt style={{ fontSize: 22, opacity: 0.85 }} /> Fleet Map
                                </div>
                                {lastUpdate && <div className="vrtm-map-updated">Last ping: {lastUpdate.toLocaleTimeString()}</div>}
                            </div>
                            <div style={{ display: 'flex', gap: 6 }}>
                                <span className="vrtm-stat-pill"><span className="vrtm-stat-dot blue" />{riders.length} Total</span>
                                <span className="vrtm-stat-pill"><span className="vrtm-stat-dot green" />{online.length} Online</span>
                                <span className="vrtm-stat-pill"><span className="vrtm-stat-dot gray" />{offline.length} Offline</span>
                            </div>
                        </div>

                        <div style={{ position: 'relative', height: 520 }}>
                            {riders.length === 0 ? (
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', flexDirection: 'column', gap: 12, color: C.gray, padding: 40, textAlign: 'center' }}>
                                    <FaMotorcycle style={{ fontSize: 48, opacity: 0.25 }} />
                                    <div style={{ fontWeight: 700 }}>No riders yet</div>
                                    <div style={{ fontSize: '0.8rem' }}>Add riders under <strong>My Riders</strong> and ask them to open the Suku Rider App — their pins will appear here once they go online.</div>
                                </div>
                            ) : (
                                <RiderFleetMap
                                    riders={ridersWithLoc}
                                    getPosition={getRiderPosition}
                                    trails={riderTrails}
                                    selectedRiderId={selectedRider?._id || null}
                                    onSelectRider={setSelectedRider}
                                    renderPopup={renderRiderPopup}
                                    height="100%"
                                />
                            )}

                            {riders.length > 0 && (
                                <div style={{ position: 'absolute', bottom: 14, left: 14, zIndex: 1000 }}>
                                    <span className="vrtm-stat-pill">
                                        <span className="vrtm-stat-dot yellow" />
                                        {ridersWithLoc.length} with live GPS
                                    </span>
                                </div>
                            )}
                        </div>
                    </Card>
                </Col>

                {/* ── Rider list panel ────────────────────────────────────────── */}
                <Col lg="4" style={{ marginBottom: 20 }}>
                    <Card style={{ borderRadius: 12, overflow: 'hidden', border: 'none', boxShadow: '0 4px 24px rgba(0,0,0,0.12)', height: 598 }}>
                        <div className="vrtm-panel-header">
                            <div className="vrtm-panel-title">
                                <span>👥</span> My Riders
                                <span style={{ background: 'rgba(255,255,255,0.15)', borderRadius: 20, padding: '1px 10px', fontSize: '0.72rem' }}>
                                    {riders.length}
                                </span>
                            </div>
                            <div style={{ display: 'flex', gap: 5 }}>
                                <span style={{ fontSize: '0.65rem', background: '#10b981', color: '#fff', borderRadius: 20, padding: '2px 8px', fontWeight: 700 }}>{online.length} online</span>
                                <span style={{ fontSize: '0.65rem', background: 'rgba(255,255,255,0.15)', color: '#fff', borderRadius: 20, padding: '2px 8px', fontWeight: 700 }}>{offline.length} offline</span>
                            </div>
                        </div>

                        <div style={{ overflowY: 'auto', flex: 1, height: 'calc(100% - 54px)' }}>
                            {riders.length === 0 ? (
                                <div className="vrtm-empty">
                                    No riders found.<br />
                                    <span style={{ fontSize: '0.75rem' }}>Add riders under <strong>My Riders</strong> first.</span>
                                </div>
                            ) : (
                                // Sort: Online first, then Busy, then Offline
                                [...riders].sort((a, b) => {
                                    const order = { Online: 0, Busy: 1, inTransit: 2 };
                                    return (order[a.onlineStatus] ?? 3) - (order[b.onlineStatus] ?? 3);
                                }).map(rider => {
                                    const rawLoc     = getLatLng(rider.currentLocation);
                                    const hasLiveGps = isFreshRiderLocation(rider.currentLocation, clockNow);
                                    const loc        = hasLiveGps ? rawLoc : null;
                                    const cacheKey   = loc ? `${loc.lat.toFixed(5)},${loc.lng.toFixed(5)}` : null;
                                    const locName    = cacheKey ? locationNames[cacheKey] : null;
                                    const sCls       = statusCls(rider.onlineStatus);
                                    const isSelected = selectedRider?._id === rider._id;
                                    return (
                                        <div
                                            key={rider._id}
                                            className={`vrtm-rider-item ${sCls}${isSelected ? ' selected' : ''}`}
                                            onClick={() => setSelectedRider(rider)}
                                        >
                                            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                                                <div className="vrtm-avatar" style={{ background: avatarColor(rider) }}>
                                                    {initials(rider)}
                                                </div>
                                                <div style={{ flex: 1, minWidth: 0 }}>
                                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                                        <div className="vrtm-rider-name">{rider.firstName} {rider.lastName}</div>
                                                        <span className={`vrtm-badge vrtm-badge-${sCls}`}>{rider.onlineStatus}</span>
                                                    </div>
                                                    <div className="vrtm-rider-sub">{carrierIcon(rider.carrierType)} {rider.carrierType}</div>
                                                    {rider.currentActiveOrders > 0 && (
                                                        <span className="vrtm-badge" style={{ background: '#fef3c7', color: '#92400e', marginTop: 3 }}>
                                                            {rider.currentActiveOrders} active order{rider.currentActiveOrders > 1 ? 's' : ''}
                                                        </span>
                                                    )}
                                                    {loc && (
                                                        <div className="vrtm-rider-loc">
                                                            {locName
                                                                ? `📍 ${locName}`
                                                                : <span style={{ color: C.gray }}>📍 {loc.lat.toFixed(5)}, {loc.lng.toFixed(5)}</span>
                                                            }
                                                        </div>
                                                    )}
                                                    {!loc && rawLoc && (
                                                        <div className="vrtm-rider-sub" style={{ marginTop: 3, color: C.yellow }}>
                                                            {riderLocationAgeLabel(rider.currentLocation, clockNow)} — waiting for a fresh ping
                                                        </div>
                                                    )}
                                                    {!loc && !rawLoc && rider.onlineStatus !== 'Online' && (
                                                        <div className="vrtm-rider-sub" style={{ marginTop: 3 }}>No location data</div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })
                            )}
                        </div>
                    </Card>
                </Col>
            </Row>
        </div>
    );
};

export default VendorRiderTrackingMap;
